/**
 * Client-side logger that intercepts console methods and sends logs to Express server
 * for Google Cloud Logging integration.
 * 
 * Features:
 * - Structured event logging with eventType
 * - Global error handlers (window.error, unhandledrejection)
 * - Global getUserMedia interception
 * - Helper functions for structured logging
 */

// Enable/disable flag for external logging
// Disabled on hosts without an Express backend (e.g. uenv.dev serves static files only)
const _hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const _hasBackend = _hostname === 'localhost' || _hostname === '127.0.0.1'
  || _hostname.includes('ustage.app') || _hostname.includes('unblu.app');
const ENABLE_EXTERNAL_LOGGING = 
  import.meta.env.VITE_ENABLE_LOGGING !== 'false' && _hasBackend;

const LOGGER_BOOT_STORAGE_KEY = 'clientLoggerBootTimestamp';
const SESSION_START_TIMESTAMP = Date.now();

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (days || hours) parts.push(`${hours}h`);
  if (days || hours || minutes) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

function getOrInitBootTimestamp(): number {
  const now = Date.now();
  try {
    const stored = window.localStorage.getItem(LOGGER_BOOT_STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    window.localStorage.setItem(LOGGER_BOOT_STORAGE_KEY, String(now));
    return now;
  } catch {
    return now;
  }
}

const BOOT_TIMESTAMP = getOrInitBootTimestamp();

// Extract api-key from URL query parameters or Unblu API object
function getApiKey(): string | null {
  try {
    // First try URL query parameter
    const url = new URL(window.location.href);
    const urlApiKey = url.searchParams.get('api-key');
    if (urlApiKey) return urlApiKey;
    
    // Fallback: try to get from Unblu API object
    const unbluApi = (window as any).unbluapi;
    if (unbluApi?.configuration?.apiKey) {
      return unbluApi.configuration.apiKey;
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

// Extract version from HTML meta tag
function getVersion(): string | null {
  try {
    const metaVersion = document.querySelector('meta[name="version"]');
    return metaVersion?.getAttribute('content') || null;
  } catch (error) {
    return null;
  }
}

// Extract branch-client-token from URL hash
function getBranchClientToken(): string | null {
  try {
    const hash = window.location.hash.substring(1); // Remove the #
    const fragmentParams = new URLSearchParams(hash);
    return fragmentParams.get('branch-client-token');
  } catch (error) {
    return null;
  }
}

// Extract branchClientKey from JWT token payload (decodes the token)
function getBranchClientKey(): string | null {
  try {
    // First try URL hash token
    const token = getBranchClientToken();
    if (token) {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length === 3) {
        // Decode payload (second part) - base64url decode
        const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        const padding = '='.repeat((4 - (payloadBase64.length % 4)) % 4);
        const payloadJson = atob(payloadBase64 + padding);
        const payload = JSON.parse(payloadJson);

        // Extract branchClientKey (this is the branch/client identifier)
        if (payload.branchClientKey) {
          return payload.branchClientKey;
        }
      }
    }
    
    // Fallback: try to get from Unblu API object and decode token
    const unbluApi = (window as any).unbluapi;
    if (unbluApi?.configuration?.branchClientToken) {
      const apiToken = unbluApi.configuration.branchClientToken;
      const parts = apiToken.split('.');
      if (parts.length === 3) {
        const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - (payloadBase64.length % 4)) % 4);
        const payloadJson = atob(payloadBase64 + padding);
        const payload = JSON.parse(payloadJson);
        if (payload.branchClientKey) {
          return payload.branchClientKey;
        }
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

// Sanitize URL by replacing branch-client-token value with ***hidden*** in hash fragment
function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;
  
  try {
    const urlObj = new URL(url);
    const hash = urlObj.hash.substring(1); // Remove the #
    
    if (hash && hash.includes('branch-client-token=')) {
      const fragmentParams = new URLSearchParams(hash);
      if (fragmentParams.has('branch-client-token')) {
        // Replace the token value with ***hidden*** (keep the parameter, just replace value)
        fragmentParams.set('branch-client-token', '***hidden***');
        urlObj.hash = '#' + fragmentParams.toString();
        return urlObj.href;
      }
    }
    
    // If URLSearchParams didn't work, fall back to regex
    // This handles edge cases where URLSearchParams might not parse correctly
    if (url.includes('#branch-client-token=')) {
      return url.replace(/#branch-client-token=[^&#]*/g, '#branch-client-token=***hidden***');
    }
    
    return urlObj.href;
  } catch (error) {
    // If URL parsing fails completely, use regex replacement as final fallback
    // Match: #branch-client-token=<anything> and replace value with ***hidden***
    // Handle both cases: at end of hash or followed by &
    if (url.includes('#branch-client-token=')) {
      return url.replace(/#branch-client-token=[^&#]*/g, '#branch-client-token=***hidden***');
    }
    return url;
  }
}

// Sanitize sensitive data from objects before logging
function sanitizeObject(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeObject(item));
    }
    
    const sanitized: any = {};
    for (const key in obj) {
      if (key === 'branchClientToken' && typeof obj[key] === 'string') {
        sanitized[key] = '***hidden***';
      } else if (key === 'url' && typeof obj[key] === 'string') {
        // Sanitize URLs to remove branch-client-token from hash
        sanitized[key] = sanitizeUrl(obj[key]);
      } else {
        sanitized[key] = sanitizeObject(obj[key]);
      }
    }
    return sanitized;
  }
  
  return obj;
}

// Map console level to Google Cloud Logging severity
function mapLevelToSeverity(level: string): string {
  const levelUpper = level.toUpperCase();
  if (levelUpper === 'WARN' || levelUpper === 'WARNING') return 'WARNING';
  if (levelUpper === 'ERROR') return 'ERROR';
  if (levelUpper === 'INFO') return 'INFO';
  if (levelUpper === 'DEBUG') return 'DEBUG';
  return 'INFO'; // Default to INFO for 'log' and unknown levels
}

// Send log to Express /data endpoint
async function sendToServer(
  level: string, 
  args: any[], 
  eventType?: string,
  urlSuffix?: string
): Promise<void> {
  if (!ENABLE_EXTERNAL_LOGGING) return;
  
  try {
    // IMPORTANT: Extract apiKey and branchClientKey BEFORE sanitizing the URL
    // These functions read from window.location.href and window.location.hash
    // We need the token value to extract branchClientKey, then we sanitize it
    const apiKey = getApiKey();
    const branchClientKey = getBranchClientKey();
    const version = getVersion();
    
    const message = args
      .map(arg => {
        if (typeof arg === 'object') {
          try {
            const sanitized = sanitizeObject(arg);
            return JSON.stringify(sanitized);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');

    // Prepend apiKey and branchClientKey to message for easy filtering
    const messageWithContext = [
      apiKey ? `[apiKey:${apiKey}]` : '',
      branchClientKey ? `[branchClientKey:${branchClientKey}]` : '',
      message
    ].filter(Boolean).join(' ');

    // Construct URL path with apiKey and branchClientKey for better log filtering
    // Format: /data/<apiKey>-<branchClientKey>[-<urlSuffix>] or /data/<apiKey>[-<urlSuffix>] or /data/<branchClientKey>[-<urlSuffix>] or /data/unknown[-<urlSuffix>]
    let dataPath = '/data';
    if (apiKey && branchClientKey) {
      dataPath = `/data/${apiKey}-${branchClientKey}`;
    } else if (apiKey) {
      dataPath = `/data/${apiKey}`;
    } else if (branchClientKey) {
      dataPath = `/data/${branchClientKey}`;
    } else {
      dataPath = '/data/unknown';
    }
    
    // Append URL suffix if provided (e.g., -NO-LOGITECH-CAM-FOUND, -DEVICE-UNPLUGGED)
    if (urlSuffix) {
      dataPath = `${dataPath}-${urlSuffix}`;
    }

    // Map level to Google Cloud Logging severity
    const severity = mapLevelToSeverity(level);

    // Send to Express server
    const now = Date.now();
    const uptimeSinceBootMs = now - BOOT_TIMESTAMP;
    const uptimeSinceRefreshMs = now - SESSION_START_TIMESTAMP;
    const uptimeSinceBootFormatted = formatDuration(uptimeSinceBootMs);
    const uptimeSinceRefreshFormatted = formatDuration(uptimeSinceRefreshMs);

    await fetch(dataPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        level,
        severity, // Google Cloud Logging severity (WARNING, ERROR, INFO, DEBUG)
        eventType: eventType || undefined, // Optional eventType for structured logging
        message: messageWithContext,
        metadata: {
          apiKey: apiKey, // Identifies Unblu installation
          branchClientKey: branchClientKey, // Identifies branch/client (e.g., "reception-1")
          version: version, // Application version (e.g., "v0.0.55")
          url: sanitizeUrl(window.location.href), // Sanitized URL with branch-client-token replaced with ***hidden*** (after extraction above)
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          uptimeSinceBootMs,
          uptimeSinceBootFormatted,
          uptimeSinceRefreshMs,
          uptimeSinceRefreshFormatted,
        },
      }),
    });
  } catch (error) {
    // Silent fail - don't break app if logging fails
    // Could optionally log to console.error but might cause recursion
  }
}

// Store original console methods
const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
  debug: console.debug,
};

// ---- Verbose mode: ?vlog=1 in URL or localStorage ----
function resolveVerbose(): boolean {
  try {
    const params = new URLSearchParams(window.location.search)
    const urlVal = params.get('vlog')
    if (urlVal === '1') {
      localStorage.setItem('vlog', '1')
      return true
    }
    if (urlVal === '0') {
      localStorage.removeItem('vlog')
      return false
    }
    return localStorage.getItem('vlog') === '1'
  } catch { return false }
}

const VERBOSE = resolveVerbose()

if (typeof window !== 'undefined') {
  ;(window as any).setVerbose = (on: boolean) => {
    if (on) localStorage.setItem('vlog', '1')
    else localStorage.removeItem('vlog')
    originalConsole.log(`Verbose logging ${on ? 'ON' : 'OFF'} — reload to apply`)
  }
}

// ---- Dedup / rate-limit for repeated console messages ----
const DEDUP_WINDOW_MS = 30_000;
const DEDUP_MAX_SUPPRESSED = 100;

/** Min gap between identical GCP posts per level (0 = always send). */
const GCP_THROTTLE_MS: Record<string, number> = {
  debug: 120_000,
  log: 60_000,
  info: 60_000,
  warn: 0,
  error: 0,
};

const _gcpThrottleLast = new Map<string, number>();

function shouldSendLevelToGcp(level: string, args: any[]): boolean {
  const minGap = GCP_THROTTLE_MS[level] ?? 0;
  if (minGap === 0) return true;
  const key = dedupFingerprint(level, args);
  const now = Date.now();
  const last = _gcpThrottleLast.get(key) ?? 0;
  if (now - last < minGap) return false;
  _gcpThrottleLast.set(key, now);
  return true;
}

const _externalEventLast = new Map<string, number>();

function logExternalThrottled(eventType: string, data: any, minGapMs: number): void {
  const last = _externalEventLast.get(eventType) ?? 0;
  if (Date.now() - last < minGapMs) return;
  _externalEventLast.set(eventType, Date.now());
  logExternal(eventType, data);
}

let _dedupKey: string | null = null;
let _dedupLevel: string | null = null;
let _dedupCount = 0;
let _dedupLastTs = 0;
let _dedupTimer: ReturnType<typeof setTimeout> | null = null;

function dedupFingerprint(level: string, args: any[]): string {
  const first = args.length > 0 ? String(args[0]) : '';
  return `${level}::${first.slice(0, 200)}`;
}

function flushDedup(): void {
  if (_dedupTimer) { clearTimeout(_dedupTimer); _dedupTimer = null; }
  if (_dedupCount > 0 && _dedupLevel) {
    const method = originalConsole[_dedupLevel as keyof typeof originalConsole] || originalConsole.log;
    const msg = `\u2191 repeated ${_dedupCount} time${_dedupCount > 1 ? 's' : ''} (suppressed)`;
    method(msg);
    sendToServer(_dedupLevel, [msg], undefined, 'DEDUP-SUPPRESSED');
  }
  _dedupKey = null;
  _dedupLevel = null;
  _dedupCount = 0;
  _dedupLastTs = 0;
}

/**
 * Returns true if the message is a duplicate and was suppressed.
 * Suppresses both console output and sendToServer for dupes.
 * Emits a summary after a quiet gap or every DEDUP_MAX_SUPPRESSED occurrences.
 */
function dedupCheck(level: string, args: any[]): boolean {
  const key = dedupFingerprint(level, args);
  const now = Date.now();

  if (key === _dedupKey && (now - _dedupLastTs) < DEDUP_WINDOW_MS) {
    _dedupCount++;
    _dedupLastTs = now;
    if (_dedupCount >= DEDUP_MAX_SUPPRESSED) {
      flushDedup();
      _dedupKey = key;
      _dedupLevel = level;
      _dedupLastTs = now;
    }
    if (_dedupTimer) clearTimeout(_dedupTimer);
    _dedupTimer = setTimeout(flushDedup, DEDUP_WINDOW_MS);
    return true;
  }

  flushDedup();
  _dedupKey = key;
  _dedupLevel = level;
  _dedupCount = 0;
  _dedupLastTs = now;
  _dedupTimer = setTimeout(flushDedup, DEDUP_WINDOW_MS);
  return false;
}

// Enhanced helper function for structured event logging
export async function logExternalAsync(
  eventType: string,
  data: any = {}
): Promise<void> {
  if (!ENABLE_EXTERNAL_LOGGING) return;
  
  try {
    // Sanitize data before logging
    const sanitizedData = sanitizeObject(data);
    
    const requestPayload = {
      eventType,
      ...sanitizedData,
      timestamp: Date.now(),
    };
    
    // Send as structured log with eventType
    // sendToServer will automatically include apiKey, branchClientKey, version in metadata
    await sendToServer('log', [JSON.stringify(requestPayload)], eventType);
  } catch (error) {
    // Silent fail - don't break app if logging fails
  }
}

// Helper function for structured event logging (fire and forget)
export function logExternal(eventType: string, data: any = {}): void {
  if (!ENABLE_EXTERNAL_LOGGING) return;
  
  logExternalAsync(eventType, data)
    .catch(e => {
      originalConsole.error('log to external failed', e.message, e);
    });
}

// Initialize global error handlers
function initGlobalErrorHandlers(): void {
  if (!ENABLE_EXTERNAL_LOGGING) return;
  
  // Global window error handler
  window.addEventListener('error', (event) => {
    logExternal('windowError', {
      errorData: {
        type: event.type,
        message: event.message,
        colno: event.colno,
        lineno: event.lineno,
        filename: event.filename,
        stack: event.error?.stack
      }
    });
  });
  
  // Global unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logExternal('unhandledPromiseRejection', {
      reason: event.reason,
      error: event.reason?.stack || String(event.reason)
    });
  });
}

// Initialize global getUserMedia interception
function initGetUserMediaInterception(): void {
  if (!ENABLE_EXTERNAL_LOGGING) return;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
  
  try {
    const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    
    navigator.mediaDevices.getUserMedia = async function(options) {
      try {
        const result = await originalGetUserMedia(options);
        logExternalThrottled(
          'getUserMedia-success',
          { options: sanitizeObject(options) },
          900_000
        );
        return result;
      } catch (e: any) {
        logExternal('getUserMedia-failed', {
          name: e.name,
          message: e.message,
          options: sanitizeObject(options)
        });
        throw e;
      }
    };
  } catch (error) {
    // If interception fails, don't break the app
    originalConsole.warn('Failed to intercept getUserMedia:', error);
  }
}

// Tracks which suffixes have already been shown in the console.
const _shownSuffixes = new Set<string>();
const SUFFIX_GCP_THROTTLE_MS = 900_000; // 15 min between repeat suffix posts (warn/error exempt)
const _suffixGcpLast = new Map<string, number>();

// Helper function to log with URL suffix
export function logWithSuffix(level: 'log' | 'warn' | 'error' | 'info' | 'debug', urlSuffix: string, ...args: any[]): void {
  const key = `${level}:${urlSuffix}`;
  const firstTime = !_shownSuffixes.has(key);
  if (firstTime) _shownSuffixes.add(key);

  if (firstTime) {
    if (!VERBOSE && (level === 'debug' || level === 'log' || level === 'info')) {
      // Silent in console when not verbose
    } else {
      originalConsole[level](...args);
    }
  } else if (level === 'warn' || level === 'error') {
    originalConsole.debug(`\u21BB ${urlSuffix} (sent to server)`);
  }

  const alwaysGcp = level === 'warn' || level === 'error';
  if (!alwaysGcp) {
    const last = _suffixGcpLast.get(key) ?? 0;
    if (Date.now() - last < SUFFIX_GCP_THROTTLE_MS) return;
    _suffixGcpLast.set(key, Date.now());
  }
  sendToServer(level, args, undefined, urlSuffix);
}

// Always show in console regardless of verbose flag (for READY banner etc.)
export function logAlways(...args: any[]): void {
  originalConsole.log(...args);
  sendToServer('log', args);
}

// Initialize client logger - intercept console methods
export function initClientLogger(): void {
  console.log = (...args: any[]) => {
    if (dedupCheck('log', args)) return;
    if (VERBOSE) originalConsole.log(...args);
    if (shouldSendLevelToGcp('log', args)) sendToServer('log', args);
  };

  console.error = (...args: any[]) => {
    if (dedupCheck('error', args)) return;
    originalConsole.error(...args);
    sendToServer('error', args);
  };

  console.warn = (...args: any[]) => {
    if (dedupCheck('warn', args)) return;
    originalConsole.warn(...args);
    sendToServer('warn', args);
  };

  console.info = (...args: any[]) => {
    if (dedupCheck('info', args)) return;
    if (VERBOSE) originalConsole.info(...args);
    if (shouldSendLevelToGcp('info', args)) sendToServer('info', args);
  };

  console.debug = (...args: any[]) => {
    if (dedupCheck('debug', args)) return;
    if (VERBOSE) originalConsole.log(...args);
    if (shouldSendLevelToGcp('debug', args)) sendToServer('debug', args);
  };

  // Initialize global error handlers
  initGlobalErrorHandlers();
  
  // Initialize getUserMedia interception
  initGetUserMediaInterception();

  // Log version and detect build changes
  detectBuildChanges();

  originalConsole.log(`Client logger initialized${VERBOSE ? ' (verbose)' : ''} — add ?vlog=1 for full logs`);
}

/**
 * Detect if build/version changed and log version information
 */
function detectBuildChanges(): void {
  const version = getVersion()
  const buildTimestamp = getBuildTimestamp()
  
  // Get current script filename (contains hash for cache busting in production)
  const currentScript = getCurrentScriptFilename()
  
  // Detect if we're in dev mode
  // Dev mode indicators:
  // 1. Script filename starts with "dev:" (marked by our detection)
  // 2. Script contains "/src/" path (source files, not built)
  // 3. Script is "index.js" without hash (production should have hash like "index-abc123.js" or "index-ClZ5NcZR.js")
  // 4. Script has query parameter with timestamp (Vite dev mode adds ?t=timestamp)
  // Vite hash format: alphanumeric, can be uppercase/lowercase (e.g., "ClZ5NcZR", "abc123def")
  const hasHash = currentScript && /index-[a-zA-Z0-9]{8,}\.js/.test(currentScript)
  const hasDevQueryParam = currentScript && currentScript.includes('?t=')
  const isDevMode = !currentScript || 
                    currentScript.startsWith('dev:') ||
                    currentScript.includes('/src/') ||
                    currentScript === 'index.js' ||
                    hasDevQueryParam ||
                    (currentScript.includes('index') && !hasHash)
  
  // Get stored build info from previous session
  const storedBuildInfo = getStoredBuildInfo()
  
  // Log version information
  if (version) {
    logWithSuffix('log', 'VERSION-INFO', `Application version: ${version}`)
  }
  if (buildTimestamp) {
    logWithSuffix('log', 'BUILD-INFO', `Build timestamp: ${buildTimestamp}`)
  }
  
  // In dev mode, skip build change detection (no hashing happens)
  if (isDevMode) {
    logWithSuffix('debug', 'DEV-MODE', 'Dev mode - build change detection disabled')
    if (currentScript) {
      originalConsole.log(`Current script: ${currentScript}`)
    }
    return
  }
  
  // Production mode: check if build changed
  const buildChanged = storedBuildInfo && (
    storedBuildInfo.scriptFilename !== currentScript ||
    storedBuildInfo.version !== version
  )
  
  // Log build change detection
  if (buildChanged && storedBuildInfo) {
    logWithSuffix('log', 'NEW-BUILD-DETECTED', 'NEW BUILD DETECTED!', {
      previousVersion: storedBuildInfo.version,
      currentVersion: version,
      previousScript: storedBuildInfo.scriptFilename,
      currentScript: currentScript,
      previousBuildTime: storedBuildInfo.buildTimestamp,
      currentBuildTime: buildTimestamp
    })
  } else if (!storedBuildInfo) {
    logWithSuffix('log', 'FIRST-LOAD', 'First load - build info stored', {
      version: version,
      scriptFilename: currentScript,
      buildTimestamp: buildTimestamp
    })
  } else {
    logWithSuffix('log', 'BUILD-UNCHANGED', 'Build unchanged - using cached assets', {
      version: version,
      scriptFilename: currentScript
    })
  }
  
  // Store current build info (only in production)
  if (!isDevMode) {
    storeBuildInfo({
      version: version || 'unknown',
      scriptFilename: currentScript || 'unknown',
      buildTimestamp: buildTimestamp || 'unknown',
      detectedAt: new Date().toISOString()
    })
  }
}

/**
 * Get current script filename (with hash) from the page
 * Works in both dev mode (source files) and production (hashed files)
 */
function getCurrentScriptFilename(): string | null {
  try {
    // Find the main script tag (module type) - prioritize built assets
    const scripts = document.querySelectorAll('script[type="module"]')
    for (const script of scripts) {
      const src = script.getAttribute('src')
      if (!src) continue
      
      // Production build: look for /assets/index-[hash].js
      if (src.includes('/assets/') && src.includes('index')) {
        const filename = src.split('/').pop() || null
        if (filename && filename.includes('-')) {
          // Has hash in filename (production build)
          return filename
        }
      }
      
      // Dev mode: look for /src/main.ts or similar
      if (src.includes('/src/')) {
        const filename = src.split('/').pop() || null
        if (filename) {
          return `dev:${filename}` // Mark as dev mode
        }
      }
    }
    
    // Fallback: try to get from import.meta (but this gives source file, not built file)
    // Only use this if we're in dev mode and haven't found anything
    try {
      if ((import.meta as any)?.url) {
        const url = new URL((import.meta as any).url)
        const pathname = url.pathname
        // Only use if it's a source file (dev mode indicator)
        if (pathname.includes('/src/') || pathname.includes('.ts')) {
          const filename = pathname.split('/').pop() || null
          return filename ? `dev:${filename}` : null
        }
      }
    } catch (error) {
      // import.meta not available
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Get build timestamp from meta tag
 */
function getBuildTimestamp(): string | null {
  try {
    const metaTimestamp = document.querySelector('meta[name="build-timestamp"]')
    return metaTimestamp?.getAttribute('content') || null
  } catch (error) {
    return null
  }
}

/**
 * Get stored build info from localStorage
 */
function getStoredBuildInfo(): {
  version: string
  scriptFilename: string
  buildTimestamp: string
  detectedAt: string
} | null {
  try {
    const stored = localStorage.getItem('appBuildInfo')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    // Ignore parse errors
  }
  return null
}

/**
 * Store build info to localStorage
 */
function storeBuildInfo(info: {
  version: string
  scriptFilename: string
  buildTimestamp: string
  detectedAt: string
}): void {
  try {
    localStorage.setItem('appBuildInfo', JSON.stringify(info))
  } catch (error) {
    // Ignore storage errors (private browsing, etc.)
  }
}

